// src/context/SettingsContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsApi } from '../api/settingsApi';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Helper to get full logo URL
const getFullLogoUrl = (logoPath) => {
  if (!logoPath || typeof logoPath !== 'string') return null;
  
  const cleanedPath = logoPath.trim();
  
  // If already a full URL, return as is
  if (cleanedPath.startsWith('http://') || cleanedPath.startsWith('https://')) {
    return cleanedPath;
  }
  
  // If it starts with /, prepend base URL
  if (cleanedPath.startsWith('/')) {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.goscitech.com';
    return `${baseUrl}${cleanedPath}`;
  }
  
  // Default: prepend base URL with /uploads/
  const baseUrl = process.env.REACT_APP_API_URL || 'https://api.goscitech.com';
  return `${baseUrl}/uploads/${cleanedPath}`;
};

// Parse boolean from database (tinyint 0/1 or string)
const parseBool = (value) => {
  if (value === undefined || value === null) return false;
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    if (lower === 'true' || lower === '1') return true;
    if (lower === 'false' || lower === '0' || lower === '') return false;
  }
  return Boolean(value);
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPublicSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await settingsApi.getPublicSettings();
      
      // Handle different response formats
      let settingsData = {};
      if (response?.settings) {
        settingsData = response.settings;
      } else if (response?.data?.settings) {
        settingsData = response.data.settings;
      } else if (response?.data) {
        settingsData = response.data;
      } else {
        settingsData = response || {};
      }

      // Fix logo URL
      if (settingsData.siteLogo) {
        settingsData.siteLogo = getFullLogoUrl(settingsData.siteLogo);
      }

      setSettings(settingsData);
    } catch (err) {
      console.error('Failed to load public settings:', err);
      setError('Failed to load system settings');
      setSettings(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicSettings();
  }, []);

  const value = {
    settings,
    loading,
    error,
    fetchPublicSettings,
    
    // ============================================================
    // GENERAL SETTINGS
    // ============================================================
    siteName: settings?.siteName || '',
    siteLogo: settings?.siteLogo || null,
    siteDescription: settings?.siteDescription || '',
    maintenanceMode: parseBool(settings?.maintenanceMode),
    currency: settings?.currency || '',
    language: settings?.language || '',
    timezone: settings?.timezone || '',
    
    // ============================================================
    // PAYMENT SETTINGS
    // ============================================================
    siinqeeEnabled: parseBool(settings?.siinqeeEnabled),
    siinqeeMerchantId: settings?.siinqeeMerchantId || '',
    siinqeeApiKey: settings?.siinqeeApiKey || '',
    siinqeeApiSecret: settings?.siinqeeApiSecret || '',
    siinqeeEnvironment: settings?.siinqeeEnvironment || 'sandbox',
    cashOnDeliveryEnabled: parseBool(settings?.cashOnDeliveryEnabled),
    
    // ============================================================
    // VENDOR SETTINGS
    // ============================================================
    requireVendorApproval: parseBool(settings?.requireVendorApproval),
    autoAcceptOrders: parseBool(settings?.autoAcceptOrders),
    commissionRate: settings?.commissionRate || 0,
    minimumPayout: settings?.minimumPayout || 0,
    enableReviews: parseBool(settings?.enableReviews),
    showVendorInfo: parseBool(settings?.showVendorInfo),
    
    // ============================================================
    // EMAIL SETTINGS
    // ============================================================
    smtpHost: settings?.smtpHost || '',
    smtpPort: settings?.smtpPort || 587,
    smtpSecure: parseBool(settings?.smtpSecure),
    smtpUser: settings?.smtpUser || '',
    smtpPass: settings?.smtpPass || '',
    emailNotifications: parseBool(settings?.emailNotifications),
    orderNotifications: parseBool(settings?.orderNotifications),
    lowStockAlerts: parseBool(settings?.lowStockAlerts),
    marketingEmails: parseBool(settings?.marketingEmails),
    
    // ============================================================
    // SECURITY SETTINGS
    // ============================================================
    enableTwoFactor: parseBool(settings?.enableTwoFactor),
    sessionTimeout: settings?.sessionTimeout || 60,
    debugMode: parseBool(settings?.debugMode),
    cacheEnabled: parseBool(settings?.cacheEnabled),
    minimumStockAlert: settings?.minimumStockAlert || 10,
    updatedBy: settings?.updatedBy || null,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;